import { ROUTE_TYPE_NAME, SEGMENT_TYPE_NAME } from '@lp/features/RoutePlanner/components/LogisticsChangeLog/consts';
import { getCurrentEventValues, unwrapModifiedEventLog } from './helpers';
import { LogisticsChangeLog, LogisticsChangeLogType, RouteTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';

const getProviderTailNumber = (log: LogisticsChangeLog) => {
  switch (log.$type) {
    case LogisticsChangeLogType.SupplementalLiftAviaLegAddedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegRemovedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegRemovedLog:
      return log.tailNumber;
    case LogisticsChangeLogType.SupplementalLiftAviaLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegModifiedLog:
      return log.leg.tailNumber ? getCurrentEventValues(log.leg.tailNumber) : '';
    default:
      return '';
  }
};

const getFlightNumber = (log: LogisticsChangeLog) => {
  switch (log.$type) {
    case LogisticsChangeLogType.SabreCommercialAviaLegsAddedLog:
    case LogisticsChangeLogType.SabreCommercialAviaGroupRemovedLog:
      return log.legs.map((leg) => leg.flightNumber).join(', ');
    case LogisticsChangeLogType.CommercialFlightAviaLegAddedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegRemovedLog:
      return log.flightNumber;
    case LogisticsChangeLogType.CommercialFlightAviaLegModifiedLog:
    case LogisticsChangeLogType.SabreCommercialAviaLegModifiedLog:
      return log.leg.flightNumber ? getCurrentEventValues(log.leg.flightNumber) : '';
    default:
      return '';
  }
};

const getProviderName = (log: LogisticsChangeLog) => {
  switch (log.$type) {
    case LogisticsChangeLogType.SupplementalLiftAviaLegModifiedLog:
      return unwrapModifiedEventLog(log.leg.provider).name;
    case LogisticsChangeLogType.TxCenterProviderAviaLegModifiedLog:
      return unwrapModifiedEventLog(log.leg.provider).name;
    case LogisticsChangeLogType.TxCenterProviderGroundLegModifiedLog:
      return unwrapModifiedEventLog(log.leg.provider).name;
    case LogisticsChangeLogType.SupplementalLiftAviaLegAddedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegRemovedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegRemovedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegRemovedLog:
      return log.providerName;
    default:
      return '';
  }
};

export const getLogisticsLogTypeName = (logType: LogisticsChangeLogType) => {
  switch (logType) {
    case LogisticsChangeLogType.TmdxJetAviaLegAddedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegAddedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegAddedLog:
      return 'Avia leg added';
    case LogisticsChangeLogType.TmdxJetAviaLegModifiedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegModifiedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegModifiedLog:
      return 'Avia leg updated';
    case LogisticsChangeLogType.TmdxJetAviaLegRemovedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegRemovedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegRemovedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegRemovedLog:
      return 'Avia leg removed';
    case LogisticsChangeLogType.AccommodationLegAddedLog:
      return 'Accommodation leg added';
    case LogisticsChangeLogType.AccommodationLegModifiedLog:
      return 'Accommodation leg updated';
    case LogisticsChangeLogType.AccommodationLegRemovedLog:
      return 'Accommodation leg removed';
    case LogisticsChangeLogType.SabreCommercialAviaLegsAddedLog:
      return 'Connected avia legs added';
    case LogisticsChangeLogType.SabreCommercialAviaLegModifiedLog:
      return 'Connected avia leg updated';
    case LogisticsChangeLogType.SabreCommercialAviaGroupRemovedLog:
      return 'Connected avia legs removed';
    case LogisticsChangeLogType.TmdxAffiliateGroundLegAddedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegAddedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegAddedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegAddedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegAddedLog:
      return 'Ground leg added';
    case LogisticsChangeLogType.TmdxAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegModifiedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegModifiedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegModifiedLog:
    case LogisticsChangeLogType.GroundTripToAffiliateLegAssignedLog:
    case LogisticsChangeLogType.GroundTripToTedsLegAssignedLog:
    case LogisticsChangeLogType.GroundTripToAffiliateLegReassignedLog:
    case LogisticsChangeLogType.GroundTripToTedsLegReassignedLog:
      return 'Ground leg updated';
    case LogisticsChangeLogType.TmdxAffiliateGroundLegRemovedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegRemovedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegRemovedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegRemovedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegRemovedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegRemovedLog:
      return 'Ground leg removed';
    case LogisticsChangeLogType.RouteDepartureHubAssignedLog:
    case LogisticsChangeLogType.RouteArrivalHubAssignedLog:
      return 'Hub added';
    case LogisticsChangeLogType.RouteDepartureHubUnassignedLog:
    case LogisticsChangeLogType.RouteArrivalHubUnassignedLog:
      return 'Hub removed';
    case LogisticsChangeLogType.RouteRemovedLog:
      return 'Route removed';
    case LogisticsChangeLogType.SuggestedRoutesAppliedLog:
      return 'Suggested route applied';
    case LogisticsChangeLogType.PartialSuggestedRoutesAppliedLog:
      return 'Partial route applied';
    case LogisticsChangeLogType.LogisticsResourceRemovedLog:
      return 'Resource removed';
    case LogisticsChangeLogType.LogisticsResourceSwappedLog:
      return 'Resource swapped';
    default:
      return '';
  }
};

export const getLogisticsLogProviderType = (logType: LogisticsChangeLogType) => {
  switch (logType) {
    case LogisticsChangeLogType.TmdxJetAviaLegAddedLog:
    case LogisticsChangeLogType.TmdxJetAviaLegModifiedLog:
    case LogisticsChangeLogType.TmdxJetAviaLegRemovedLog:
      return 'TMDX Jet';
    case LogisticsChangeLogType.SupplementalLiftAviaLegAddedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegModifiedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegRemovedLog:
      return 'Supplemental Lift Provider';
    case LogisticsChangeLogType.TxCenterProviderAviaLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegRemovedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegRemovedLog:
      return 'TX Center Provider';
    case LogisticsChangeLogType.CommercialFlightAviaLegAddedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegModifiedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegRemovedLog:
    case LogisticsChangeLogType.SabreCommercialAviaLegsAddedLog:
    case LogisticsChangeLogType.SabreCommercialAviaLegModifiedLog:
    case LogisticsChangeLogType.SabreCommercialAviaGroupRemovedLog:
      return 'Commercial Flight';
    case LogisticsChangeLogType.CurriServiceGroundLegAddedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegModifiedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegRemovedLog:
      return 'Curri Service';
    case LogisticsChangeLogType.TmdxAffiliateGroundLegAddedLog:
    case LogisticsChangeLogType.TmdxAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxAffiliateGroundLegRemovedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegAddedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegRemovedLog:
    case LogisticsChangeLogType.GroundTripToAffiliateLegAssignedLog:
    case LogisticsChangeLogType.GroundTripToAffiliateLegReassignedLog:
      return 'TMDX Affiliate';
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegAddedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegRemovedLog:
    case LogisticsChangeLogType.GroundTripToTedsLegAssignedLog:
    case LogisticsChangeLogType.GroundTripToTedsLegReassignedLog:
      return 'TEDS';
    case LogisticsChangeLogType.SelfServicedGroundLegAddedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegModifiedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegRemovedLog:
      return 'Self-Serviced';
    default:
      return '';
  }
};

export const getLogisticsLogSegmentName = (log: LogisticsChangeLog): string => {
  switch (log.$type) {
    case LogisticsChangeLogType.RouteDepartureHubAssignedLog:
    case LogisticsChangeLogType.RouteDepartureHubUnassignedLog:
      return log.routeType === RouteTypeEnum.Main ? 'Hub - DH' : '';
    case LogisticsChangeLogType.RouteArrivalHubAssignedLog:
    case LogisticsChangeLogType.RouteArrivalHubUnassignedLog:
      return log.routeType === RouteTypeEnum.Main ? 'TXC - Hub' : 'DH - Hub';
    case LogisticsChangeLogType.TmdxJetAviaLegModifiedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegModifiedLog:
    case LogisticsChangeLogType.TmdxAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegModifiedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegModifiedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegModifiedLog:
    case LogisticsChangeLogType.GroundTripToAffiliateLegReassignedLog:
    case LogisticsChangeLogType.GroundTripToTedsLegReassignedLog:
      return SEGMENT_TYPE_NAME[log.leg.segmentType];
    case LogisticsChangeLogType.CommercialFlightAviaLegAddedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegModifiedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegRemovedLog:
    case LogisticsChangeLogType.SabreCommercialAviaLegsAddedLog:
    case LogisticsChangeLogType.SabreCommercialAviaLegModifiedLog:
    case LogisticsChangeLogType.SabreCommercialAviaGroupRemovedLog:
      return SEGMENT_TYPE_NAME[SegmentTypeEnum.custom];
    case LogisticsChangeLogType.AccommodationLegAddedLog:
    case LogisticsChangeLogType.AccommodationLegModifiedLog:
    case LogisticsChangeLogType.AccommodationLegRemovedLog:
    case LogisticsChangeLogType.RouteRemovedLog:
    case LogisticsChangeLogType.SuggestedRoutesAppliedLog:
    case LogisticsChangeLogType.PartialSuggestedRoutesAppliedLog:
    case LogisticsChangeLogType.LogisticsResourceSwappedLog:
    case LogisticsChangeLogType.LogisticsResourceRemovedLog:
      return '';
    default:
      return SEGMENT_TYPE_NAME[log.segmentType];
  }
};

const getRouteTypeName = (log: LogisticsChangeLog): string => {
  switch (log.$type) {
    case LogisticsChangeLogType.LogisticsResourceSwappedLog:
    case LogisticsChangeLogType.LogisticsResourceRemovedLog:
      return `${ROUTE_TYPE_NAME.Main}, ${ROUTE_TYPE_NAME.DryRun}`;
    default:
      return ROUTE_TYPE_NAME[log.routeType];
  }
};

export const getLogisticsLogHeaderItems = (log: LogisticsChangeLog): string[] => {
  const logTypeName = getLogisticsLogTypeName(log.$type);
  const routeTypeName = getRouteTypeName(log);
  const providerType = getLogisticsLogProviderType(log.$type);
  const segmentName = getLogisticsLogSegmentName(log);
  const routeTypeAndSegment = segmentName ? [routeTypeName, segmentName].join(', ') : routeTypeName;
  const mainItems = [logTypeName, routeTypeAndSegment];

  switch (log.$type) {
    case LogisticsChangeLogType.TmdxJetAviaLegAddedLog:
    case LogisticsChangeLogType.TmdxJetAviaLegRemovedLog:
      const aircraftNumber = log.resources.find((r) => r.resourceType === CaseResourceType.Aircraft)?.tailNumber;

      return [...mainItems, providerType, ...(aircraftNumber ? [aircraftNumber] : [])];
    case LogisticsChangeLogType.SupplementalLiftAviaLegAddedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegModifiedLog:
    case LogisticsChangeLogType.SupplementalLiftAviaLegRemovedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderAviaLegRemovedLog:
      const tailNumber = getProviderTailNumber(log);
      return [...mainItems, providerType, getProviderName(log), ...(tailNumber ? [tailNumber] : [])];
    case LogisticsChangeLogType.TxCenterProviderGroundLegAddedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegModifiedLog:
    case LogisticsChangeLogType.TxCenterProviderGroundLegRemovedLog:
      return [...mainItems, providerType, getProviderName(log)];
    case LogisticsChangeLogType.CommercialFlightAviaLegAddedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegModifiedLog:
    case LogisticsChangeLogType.CommercialFlightAviaLegRemovedLog:
    case LogisticsChangeLogType.SabreCommercialAviaLegsAddedLog:
    case LogisticsChangeLogType.SabreCommercialAviaLegModifiedLog:
    case LogisticsChangeLogType.SabreCommercialAviaGroupRemovedLog:
      const flightNumber = getFlightNumber(log);
      return [...mainItems, providerType, ...(flightNumber ? [flightNumber] : [])];
    case LogisticsChangeLogType.CurriServiceGroundLegAddedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegModifiedLog:
    case LogisticsChangeLogType.CurriServiceGroundLegRemovedLog:
    case LogisticsChangeLogType.TmdxAffiliateGroundLegAddedLog:
    case LogisticsChangeLogType.TmdxAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxAffiliateGroundLegRemovedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegAddedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxDriveAffiliateGroundLegRemovedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegAddedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegModifiedLog:
    case LogisticsChangeLogType.TmdxDriveTedsGroundLegRemovedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegAddedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegModifiedLog:
    case LogisticsChangeLogType.SelfServicedGroundLegRemovedLog:
    case LogisticsChangeLogType.GroundTripToAffiliateLegAssignedLog:
    case LogisticsChangeLogType.GroundTripToTedsLegAssignedLog:
    case LogisticsChangeLogType.GroundTripToAffiliateLegReassignedLog:
    case LogisticsChangeLogType.GroundTripToTedsLegReassignedLog:
      return [...mainItems, providerType];
    default:
      return mainItems;
  }
};
